import { useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Camera, Upload, Loader2, AlertTriangle, Image, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PhotoDiagnosis = () => {
  const { getAuthHeader } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      setDiagnosis(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const clearSelection = () => {
    setSelectedFile(null);
    setPreview(null);
    setDiagnosis(null);
  };

  const analyzePicture = async () => {
    if (!selectedFile) {
      toast.error('Please select an image first');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const res = await axios.post(
        `${API_URL}/api/photos/diagnose`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setDiagnosis(res.data);
      toast.success('Analysis complete');
    } catch (error) {
      console.error('Diagnosis error:', error);
      toast.error('Failed to analyze image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="photo-diagnosis">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
          Photo Log
        </h1>
        <p className="text-muted-foreground mt-1">
          Document and identify plants or livestock issues
        </p>
      </div>

      {/* Upload Area */}
      <Card className="card-farm" data-testid="upload-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" />
            Upload Photo
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!selectedFile ? (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                isDragActive 
                  ? 'border-primary bg-primary/5' 
                  : 'border-border hover:border-primary/50'
              }`}
              data-testid="dropzone"
            >
              <input {...getInputProps()} data-testid="file-input" />
              <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-medium text-foreground mb-2">
                {isDragActive ? 'Drop the image here' : 'Drag & drop an image'}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                or click to browse (JPEG, PNG, WEBP up to 10MB)
              </p>
              <Button variant="outline" className="pointer-events-none">
                Select Image
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={preview}
                  alt="Selected"
                  className="w-full max-h-80 object-contain rounded-lg bg-muted"
                  data-testid="preview-image"
                />
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={clearSelection}
                  data-testid="clear-image-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={analyzePicture}
                  className="btn-primary flex-1"
                  disabled={loading}
                  data-testid="analyze-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      Analyze Photo
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={clearSelection}
                  disabled={loading}
                >
                  Choose Different
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diagnosis Results */}
      {diagnosis && (
        <Card className="card-farm animate-slide-up" data-testid="diagnosis-result">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Image className="w-5 h-5 text-primary" />
              Analysis Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-foreground">
                {diagnosis.diagnosis}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border/60">
              Analyzed on {new Date(diagnosis.created_at).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tips */}
      <Card className="card-farm">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">Tips for Better Results</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              Take clear, well-lit photos showing the affected area
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              Include close-ups of symptoms (spots, discoloration, etc.)
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              For plants: capture both affected and healthy parts if possible
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              For livestock: show the specific area of concern clearly
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Disclaimer - Moved to bottom */}
      <Card className="card-farm bg-secondary/5 border-secondary/20">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">Observational Only</p>
              <p className="text-muted-foreground">
                This tool provides possible identifications for reference. Always consult a 
                veterinarian or agronomist for serious conditions. Not a substitute for professional guidance.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PhotoDiagnosis;
